import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, Info, MoreVertical, Printer, X } from "lucide-react";
import { toast } from "react-hot-toast";
import { printAllPoliciesAsDocument, printSinglePolicyAsDocument } from "./policySummaryPrint";
import {
  clonePolicySnapshot,
  computeVersionOnSave,
  getPolicySnapshotsForDisplay,
  mergePolicySnapshots,
} from "./confirmationPolicyDocument";
import {
  buildOnboardingPolicyDocumentHtml,
  printOnboardingPolicyDocumentHtml,
} from "./onboardingPolicyDocument";
import {
  MASTERS_DOCUMENT_COMPANY_ADDRESS,
  MASTERS_DOCUMENT_COMPANY_NAME,
} from "./masters/companyDocumentDefaults";
import api, { onboardingPolicyService } from "../../services/api";
import PolicyDeleteImpactModal from "./policy-wizard/PolicyDeleteImpactModal";
import PolicyAppliedMastersEye from "./policy-wizard/PolicyAppliedMastersEye";
import PolicyListTableHeader, {
  policyListRowActionClass,
  policyListStatusClass,
} from "./policy-wizard/PolicyListTableHeader";
import PolicyListToolbar from "./policy-wizard/PolicyListToolbar";
import { DsButton } from "../../design-system";
import { confirmPolicyDelete, startPolicyDeleteFlow } from "../../utils/policyDeleteFlow";
import {
  buildRoleSelectOptions,
  getRoleLabel,
  resolveRoleSelectValue,
  splitRoleSelectOptions,
} from "../../utils/roleSelectOptions";
import PolicyWorkflowStepSection from "./policy-wizard/PolicyWorkflowStepSection";
import PolicyChecklistStepSection from "./policy-wizard/PolicyChecklistStepSection";
import PolicyApplicabilityStepSection from "./policy-wizard/PolicyApplicabilityStepSection";
import { ONBOARDING_WORKFLOW_TABS } from "./policy-wizard/policyWizardSteps";
import {
  APPOINTMENT_LETTER_TYPE,
  getLetterTemplateLabel,
  getLetterTemplateSelectOptions,
  subscribeLetterTemplatesUpdated,
} from "../../utils/letterTemplatesStorage";

const ORG_APPLICABILITY_KEYS = [
  "branch",
  "department",
  "designation",
  "grade",
  "category",
  "subDepartment",
  "location",
];

function createDefaultPolicy() {
  return {
    policyName: "",
    effectiveDate: new Date().toISOString().slice(0, 10),
    autoAssignOnJoin: false,
    allEmployees: false,
    approvalMethod: "workflow",
    reminderDays: 7,
    needWorkflowSteps: false,
    autoLetter: "no",
    letterFormat: "",
    useCancellationLetter: false,
    cancellationLetterFormat: "",
    workflowParallelApproval: false,
    workflowEscalationAfterDays: 3,
    workflowAutoApprovalIfNoAction: false,
    workflowRejectionWithReason: true,
    workflowReinitiationAllowed: true,
    blockJoiningWithoutProfileCompletion: true,
    mandatoryProfileTabs: [
      "Organization Details",
      "Personal Details",
      "Family Details",
      "Educational Details",
      "Professional Details",
      "Bank Details",
      "Statutory Details",
      "Documents",
    ],
    workflowSteps: [],
    cancellationWorkflowSteps: [],
    checklistMappings: [],
    branch: "",
    department: "",
    designation: "",
    grade: "",
    category: "",
    subDepartment: "",
    location: "",
    individualEmployeeIds: "",
    employmentTypes: [],
    alerts: {
      hr: true,
      manager: true,
      employee: true,
    },
    policyDocumentPushed: false,
    policyPushedAt: null,
    policyVersion: 1,
    policyVersionHistory: [],
    policySnapshots: [],
  };
}

function normalizePolicyRow(row) {
  const d = createDefaultPolicy();
  const at = row.assignedTo || {};
  const policyName = row.policyName || row.name || "";
  const pickOrg = (key, assignedKey) => {
    if (row[key] != null && row[key] !== "") return String(row[key]);
    const fromAssigned = at[assignedKey]?.[0];
    return fromAssigned != null ? String(fromAssigned) : "";
  };
  return {
    ...d,
    ...row,
    policyName,
    allEmployees: !!row.allEmployees || !!at.allEmployees,
    branch: pickOrg("branch", "branches"),
    department: pickOrg("department", "departments"),
    designation: pickOrg("designation", "designations"),
    grade: pickOrg("grade", "grades"),
    category: pickOrg("category", "categories"),
    subDepartment: pickOrg("subDepartment", "subDepartments"),
    location: row.location || "",
    employmentTypes:
      Array.isArray(row.employmentTypes) && row.employmentTypes.length
        ? row.employmentTypes
        : Array.isArray(at.employmentTypes)
          ? [...at.employmentTypes]
          : [],
    individualEmployeeIds:
      row.individualEmployeeIds != null && row.individualEmployeeIds !== ""
        ? Array.isArray(row.individualEmployeeIds)
          ? row.individualEmployeeIds.join(", ")
          : String(row.individualEmployeeIds)
        : Array.isArray(at.employees) && at.employees.length
          ? at.employees.join(", ")
          : "",
    approvalMethod:
      row.approvalMethod === "on_instance" ? "workflow" : row.approvalMethod || "workflow",
    workflowSteps: Array.isArray(row.workflowSteps) ? row.workflowSteps : [],
    cancellationWorkflowSteps: Array.isArray(row.cancellationWorkflowSteps)
      ? row.cancellationWorkflowSteps
      : [],
    checklistMappings: Array.isArray(row.checklistMappings) ? row.checklistMappings : [],
    blockJoiningWithoutProfileCompletion: row.blockJoiningWithoutProfileCompletion !== false,
    mandatoryProfileTabs: Array.isArray(row.mandatoryProfileTabs) && row.mandatoryProfileTabs.length
      ? row.mandatoryProfileTabs
      : d.mandatoryProfileTabs,
    alerts: row.alerts || { hr: true, manager: true, employee: true },
    policyVersionHistory: Array.isArray(row.policyVersionHistory) ? row.policyVersionHistory : [],
    policySnapshots: Array.isArray(row.policySnapshots) ? row.policySnapshots : [],
  };
}

function buildAssignedTo(p) {
  const ids = String(p.individualEmployeeIds || "")
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const employees = ids.map((x) => {
    const n = Number(x);
    return Number.isNaN(n) ? x : n;
  });
  if (p.allEmployees) {
    return {
      allEmployees: true,
      branches: [],
      departments: [],
      designations: [],
      grades: [],
      categories: [],
      subDepartments: [],
      employmentTypes: [],
      employees: [],
    };
  }
  const pickId = (value) => {
    if (!value) return [];
    const n = Number(value);
    return [Number.isNaN(n) ? value : n];
  };
  return {
    allEmployees: false,
    branches: pickId(p.branch),
    departments: pickId(p.department),
    designations: pickId(p.designation),
    grades: pickId(p.grade),
    categories: pickId(p.category),
    subDepartments: pickId(p.subDepartment),
    employmentTypes: Array.isArray(p.employmentTypes) ? [...p.employmentTypes] : [],
    employees,
  };
}

function mergePayloadForApi(form) {
  const policyName = String(form.policyName || "").trim();
  const approvalMethod =
    form.approvalMethod === "on_instance" ? "workflow" : form.approvalMethod || "workflow";
  return {
    ...form,
    approvalMethod,
    name: policyName || form.name || "",
    policyName,
    assignedTo: buildAssignedTo(form),
    approvalRolesForPolicy: Array.isArray(form.approvalRolesForPolicy)
      ? form.approvalRolesForPolicy
      : [],
  };
}

/** Strip client-only fields before POST/PUT so Sequelize receives a clean payload. */
function buildOnboardingPolicyApiPayload(form, approvalRolesForPolicy = []) {
  const payload = mergePayloadForApi({
    ...form,
    approvalRolesForPolicy,
  });
  const {
    id: _id,
    createdAt: _createdAt,
    updatedAt: _updatedAt,
    ...apiPayload
  } = payload;
  return apiPayload;
}

function PolicyRoleSelectOptions({ roleOptions, storedValue }) {
  const { systemRoleOptions, customRoleOptions } = splitRoleSelectOptions(roleOptions);
  const resolved = resolveRoleSelectValue(roleOptions, storedValue);
  const hasResolved =
    !resolved ||
    roleOptions.some(
      (role) =>
        role.value === resolved ||
        role.value.toLowerCase() === String(resolved).toLowerCase()
    );

  if (!roleOptions.length) {
    return <option value="">Loading roles…</option>;
  }

  return (
    <>
      {!hasResolved && resolved ? (
        <option value={resolved}>{getRoleLabel(roleOptions, resolved)}</option>
      ) : null}
      {systemRoleOptions.length > 0 && (
        <optgroup label="System roles">
          {systemRoleOptions.map((role) => (
            <option key={`sys-${role.id ?? role.value}`} value={role.value}>
              {role.label}
            </option>
          ))}
        </optgroup>
      )}
      {customRoleOptions.length > 0 && (
        <optgroup label="Custom roles">
          {customRoleOptions.map((role) => (
            <option key={`custom-${role.id ?? role.value}`} value={role.value}>
              {role.label}
            </option>
          ))}
        </optgroup>
      )}
    </>
  );
}

const OnboardingPolicyComponent = ({
  darkMode,
  branchesList,
  departmentsList,
  designationsList,
  subDepartmentsList,
  gradesList,
  categoriesList,
  companyName = MASTERS_DOCUMENT_COMPANY_NAME,
  companyAddress = MASTERS_DOCUMENT_COMPANY_ADDRESS,
  policyPreparedBy = "Human Resources",
  policyApprovedBy = "Management",
  policies = [],
  onPolicyChange,
  employees = [],
  loadingPolicies = false,
}) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingPolicyId, setEditingPolicyId] = useState(null);
  const [showInactivePolicies, setShowInactivePolicies] = useState(false);
  const [policySearch, setPolicySearch] = useState("");
  const [workflowStepsTab, setWorkflowStepsTab] = useState("approval");
  const [policyFormStep, setPolicyFormStep] = useState(1);
  const [policyHelpOpen, setPolicyHelpOpen] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  /** Blocks save: effective date unchanged after edits, or applicability overlap — use modals, not only inline text. */
  const [saveGateModal, setSaveGateModal] = useState(null);
  const [policySaveError, setPolicySaveError] = useState(null);
  const [approvalSettingsOpen, setApprovalSettingsOpen] = useState(false);
  const [approvalRolesForPolicy, setApprovalRolesForPolicy] = useState(["management", "ceo"]);
  const [previewPolicy, setPreviewPolicy] = useState(null);
  const [flowchartPolicy, setFlowchartPolicy] = useState(null);
  const [policyDocModal, setPolicyDocModal] = useState(null);
  const [policyHistoryModalId, setPolicyHistoryModalId] = useState(null);
  const [historyPreviewEntry, setHistoryPreviewEntry] = useState(null);
  const [actionMenu, setActionMenu] = useState(null);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [deletePolicyModal, setDeletePolicyModal] = useState(null);
  const [deletePolicyLoading, setDeletePolicyLoading] = useState(false);
  const [companyRoles, setCompanyRoles] = useState([]);
  const [appointmentLetterOptions, setAppointmentLetterOptions] = useState(() =>
    getLetterTemplateSelectOptions(APPOINTMENT_LETTER_TYPE)
  );
  const policyHelpRef = useRef(null);
  const approvalSettingsRef = useRef(null);

  const docCompanyName =
    (typeof companyName === "string" && companyName.trim()) || MASTERS_DOCUMENT_COMPANY_NAME;
  const docCompanyAddress =
    (typeof companyAddress === "string" && companyAddress.trim()) || MASTERS_DOCUMENT_COMPANY_ADDRESS;

  useEffect(() => {
    onPolicyChange?.();
  }, [onPolicyChange]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await api.get("/global-settings/roles");
        if (!cancelled && response.data?.success) {
          setCompanyRoles(response.data.data || []);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching roles for onboarding policy:", error);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const refreshAppointmentLetters = () => {
      setAppointmentLetterOptions(getLetterTemplateSelectOptions(APPOINTMENT_LETTER_TYPE));
    };
    refreshAppointmentLetters();
    return subscribeLetterTemplatesUpdated(refreshAppointmentLetters);
  }, []);

  const policyRoleOptions = useMemo(() => buildRoleSelectOptions(companyRoles), [companyRoles]);
  const defaultPolicyRoleValue = policyRoleOptions[0]?.value || "";
  const getPolicyRoleLabel = (roleValue) => getRoleLabel(policyRoleOptions, roleValue);

  const APPROVAL_ROLES = [
    { value: "ceo", label: "CEO" },
    { value: "management", label: "Management" },
    { value: "hr", label: "HR" },
    { value: "manager", label: "Manager" },
    { value: "reporting_manager", label: "Reporting Manager" },
  ];
  const getAppointmentLetterLabel = (templateId) =>
    getLetterTemplateLabel(APPOINTMENT_LETTER_TYPE, templateId);

  const PROFILE_TAB_OPTIONS = [
    "Organization Details",
    "Personal Details",
    "Family Details",
    "Educational Details",
    "Professional Details",
    "Bank Details",
    "Statutory Details",
    "Documents",
  ];
  const CHECKLIST_TRIGGER_EVENTS = [
    { value: "onboarding_request", label: "Onboarding request" },
    { value: "document_verification", label: "Document verification" },
    { value: "onboarding_approval", label: "Onboarding approval" },
    { value: "cancellation", label: "Cancellation / withdrawal" },
    { value: "confirmation", label: "Confirmation approval (legacy)" },
    { value: "extension", label: "Extension approval (legacy)" },
    { value: "termination", label: "Termination approval (legacy)" },
  ];
  const CHECKLIST_DEPARTMENTS = [
    { value: "", label: "Select department" },
    { value: "hr", label: "HR" },
    { value: "it", label: "IT" },
    { value: "admin", label: "Admin" },
    { value: "finance", label: "Finance" },
  ];
  const DUE_DATE_LOGIC_OPTIONS = [
    { value: "approval_date", label: "From approval date" },
    { value: "fixed_days", label: "Fixed days from trigger" },
  ];
  const LOCATION_OPTIONS = [
    { value: "", label: "All Locations" },
    { value: "1", label: "Mumbai" },
    { value: "2", label: "Delhi" },
    { value: "3", label: "Bangalore" },
  ];
  /** Applicability — employment type (multi-select in UI; empty = all types). */
  const EMPLOYMENT_TYPE_OPTIONS = [
    { value: "full_time", label: "Full-time" },
    { value: "contract", label: "Contract" },
    { value: "intern", label: "Intern" },
    { value: "consultant", label: "Consultant" },
    { value: "external", label: "External" },
    { value: "part_time", label: "Part-time" },
    { value: "trainee", label: "Trainee" },
  ];
  const POLICY_FORM_STEPS = [
    { id: 1, label: "Policy basics", shortLabel: "Basics" },
    { id: 2, label: "Profile completion", shortLabel: "Profile" },
    { id: 3, label: "Workflow", shortLabel: "Workflow" },
    { id: 4, label: "Communication & letters", shortLabel: "Letters" },
    { id: 5, label: "Checklist Mapping", shortLabel: "Checklist" },
    { id: 6, label: "Applicability", shortLabel: "Applicability" },
  ];

  const policyDocumentOptions = useMemo(
    () => ({
      companyName: docCompanyName,
      companyAddress: docCompanyAddress,
      preparedBy: policyPreparedBy,
      approvedBy: policyApprovedBy,
      workflowRoleLabel: (role) => getPolicyRoleLabel(role) || role || "—",
      branchesList,
      departmentsList,
      designationsList,
      gradesList,
      categoriesList,
      subDepartmentsList,
      locationOptions: LOCATION_OPTIONS,
      letterFormatOptions: appointmentLetterOptions,
      employmentTypeOptions: EMPLOYMENT_TYPE_OPTIONS,
    }),
    [
      docCompanyName,
      docCompanyAddress,
      policyPreparedBy,
      policyApprovedBy,
      branchesList,
      departmentsList,
      designationsList,
      gradesList,
      categoriesList,
      subDepartmentsList,
      policyRoleOptions,
      appointmentLetterOptions,
    ]
  );

  const [newPolicy, setNewPolicy] = useState(createDefaultPolicy());

  const normalizeChecklistItem = (item = {}) => ({
    triggerEvent: item.triggerEvent || "onboarding_request",
    department: item.department || "",
    actionRole: item.actionRole || defaultPolicyRoleValue || "HR",
    name: item.name || "",
    mandatory: typeof item.mandatory === "boolean" ? item.mandatory : true,
    dueDateLogic: item.dueDateLogic || "approval_date",
    dueDateDays: typeof item.dueDateDays === "number" ? item.dueDateDays : 7,
  });

  const openCreateForm = () => {
    setIsEditing(false);
    setEditingPolicyId(null);
    setWorkflowStepsTab("approval");
    setPolicyFormStep(1);
    setPolicySaveError(null);
    setPolicyHelpOpen(false);
    setSaveGateModal(null);
    setNewPolicy(createDefaultPolicy());
    setIsFormOpen(true);
  };

  const openEditForm = (policy) => {
    setIsEditing(true);
    setEditingPolicyId(policy.id);
    setWorkflowStepsTab("approval");
    setPolicyFormStep(1);
    setPolicySaveError(null);
    setPolicyHelpOpen(false);
    setSaveGateModal(null);
    const normalized = normalizePolicyRow(policy);
    setNewPolicy({
      ...normalized,
      useCancellationLetter: !!normalized.useCancellationLetter,
      checklistMappings: (normalized.checklistMappings || []).map(normalizeChecklistItem),
    });
    if (Array.isArray(policy.approvalRolesForPolicy)) {
      setApprovalRolesForPolicy(policy.approvalRolesForPolicy);
    }
    setIsFormOpen(true);
  };

  useEffect(() => {
    if (!policyHelpOpen) return;
    const onMouseDown = (e) => {
      if (policyHelpRef.current && !policyHelpRef.current.contains(e.target)) {
        setPolicyHelpOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [policyHelpOpen]);

  useEffect(() => {
    if (!approvalSettingsOpen) return;
    const onMouseDown = (e) => {
      if (approvalSettingsRef.current && !approvalSettingsRef.current.contains(e.target)) {
        setApprovalSettingsOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [approvalSettingsOpen]);

  useEffect(() => {
    if (!actionMenu) return;
    const close = () => setActionMenu(null);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [actionMenu]);

  const requestSavePolicyForm = (opts = {}) => {
    const skipOverlapCheck = !!opts.skipOverlapCheck;
    setPolicySaveError(null);
    setSaveGateModal(null);
    const normalizedIndividualIds = String(newPolicy.individualEmployeeIds || "")
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .join(", ");
    const candidate = {
      ...newPolicy,
      individualEmployeeIds: normalizedIndividualIds,
      employmentTypes: Array.isArray(newPolicy.employmentTypes) ? newPolicy.employmentTypes : [],
    };

    if (isEditing && editingPolicyId) {
      const saved = policies.find((p) => p.id === editingPolicyId);
      if (saved) {
        const sigOld = policyContentSignature(normalizePolicyRow(saved));
        const sigNew = policyContentSignature({ ...candidate, id: saved.id });
        if (sigOld !== sigNew && (saved.effectiveDate || "") === (candidate.effectiveDate || "")) {
          setSaveGateModal({ type: "effective_date" });
          return;
        }
      }
    }

    if (!skipOverlapCheck) {
      const conflicts = findConflictingPolicies(
        { ...candidate, id: candidate.id || editingPolicyId || "__new__" },
        policies.map((p) => normalizePolicyRow(p)),
        isEditing ? editingPolicyId : null
      );
      if (conflicts.length > 0) {
        setSaveGateModal({
          type: "overlap",
          items: conflicts.map((p) => ({
            id: p.id,
            name: p.policyName || p.name || "Untitled policy",
            detail: summarizeOverlapReason(candidate, p),
          })),
        });
        return;
      }
    }

    if (!String(newPolicy.policyName || "").trim()) {
      setPolicySaveError("Policy name is required.");
      return;
    }
    if (
      newPolicy.approvalMethod === "workflow" &&
      (!newPolicy.workflowSteps || newPolicy.workflowSteps.length === 0)
    ) {
      setPolicySaveError("Add at least one workflow step for 'Through workflow'.");
      return;
    }
    if (newPolicy.approvalMethod === "workflow") {
      const steps = Array.isArray(newPolicy.workflowSteps) ? newPolicy.workflowSteps : [];
      const hasApprovalStep = steps.some((s) => s?.role && !s.notifyOnly);
      if (steps.length > 0 && !hasApprovalStep) {
        setPolicySaveError(
          "All workflow steps are 'Notify only'. Uncheck Notify only on at least one step (e.g. Reporting Manager) so that role must approve — otherwise requests auto-complete and the manager only gets an FYI card."
        );
        return;
      }
    }
    setShowSummaryModal(true);
  };

  const continueAfterOverlapAck = () => {
    setSaveGateModal(null);
    requestSavePolicyForm({ skipOverlapCheck: true });
  };

  const handleSavePolicy = async () => {
    const normalizedIndividualIds = String(newPolicy.individualEmployeeIds || "")
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .join(", ");
    const form = {
      ...newPolicy,
      individualEmployeeIds: normalizedIndividualIds,
      employmentTypes: Array.isArray(newPolicy.employmentTypes) ? newPolicy.employmentTypes : [],
    };

    if (isEditing && editingPolicyId) {
      const saved = policies.find((p) => p.id === editingPolicyId);
      if (saved) {
        const sigOld = policyContentSignature(normalizePolicyRow(saved));
        const sigNew = policyContentSignature({ ...form, id: saved.id });
        if (sigOld !== sigNew && (saved.effectiveDate || "") === (form.effectiveDate || "")) {
          toast.error(
            "Policy content changed without a new effective date. Update the effective date before saving."
          );
          return;
        }
      }
    }

    const basePayload = buildOnboardingPolicyApiPayload(form, approvalRolesForPolicy);
    const savedAt = new Date().toISOString();
    let merged;

    setSavingPolicy(true);
    try {
      if (isEditing && editingPolicyId) {
        const prev = policies.find((p) => p.id === editingPolicyId);
        if (!prev) {
          toast.error("Policy not found");
          return;
        }
        const v = computeVersionOnSave(normalizePolicyRow(prev), basePayload, false);
        merged = {
          ...normalizePolicyRow(prev),
          ...basePayload,
          policyVersion: v.policyVersion,
          policyVersionHistory: v.policyVersionHistory,
          updatedAt: savedAt,
          policyDocumentPushed: prev.policyDocumentPushed,
          policyPushedAt: prev.policyPushedAt,
        };
        merged.policySnapshots = mergePolicySnapshots(
          normalizePolicyRow(prev),
          merged,
          v,
          false,
          savedAt
        );
        const res = await onboardingPolicyService.updateOnboardingPolicy(editingPolicyId, merged);
        if (res.success) {
          toast.success(
            v.versionBumped
              ? "Policy saved. Document version increased (new effective date is in the future and after the previous effective date)."
              : "Onboarding policy updated"
          );
          onPolicyChange?.(res.data);
          setIsFormOpen(false);
          setIsEditing(false);
          setEditingPolicyId(null);
          setPolicyFormStep(1);
          setShowSummaryModal(false);
        } else {
          toast.error(res.message || "Update failed");
        }
      } else {
        const v = computeVersionOnSave(null, basePayload, true);
        merged = {
          ...basePayload,
          policyVersion: v.policyVersion,
          policyVersionHistory: v.policyVersionHistory,
          status: "active",
          createdAt: savedAt,
          updatedAt: savedAt,
          policyDocumentPushed: false,
          policyPushedAt: null,
        };
        merged.policySnapshots = mergePolicySnapshots(null, merged, v, true, savedAt);
        const res = await onboardingPolicyService.createOnboardingPolicy(merged);
        if (res.success) {
          toast.success("Onboarding policy created");
          onPolicyChange?.(res.data);
          setIsFormOpen(false);
          setIsEditing(false);
          setEditingPolicyId(null);
          setPolicyFormStep(1);
          setShowSummaryModal(false);
        } else {
          toast.error(res.message || "Create failed");
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Save failed");
    } finally {
      setSavingPolicy(false);
    }
  };

  const handleCancelPolicy = () => {
    setIsFormOpen(false);
    setIsEditing(false);
    setEditingPolicyId(null);
    setWorkflowStepsTab("approval");
    setPolicyFormStep(1);
    setPolicyHelpOpen(false);
    setPolicySaveError(null);
    setSaveGateModal(null);
  };

  const validatePolicyFormStep = (step) => {
    if (step === 1) {
      if (!String(newPolicy.policyName || "").trim()) {
        return "Policy name is required before continuing.";
      }
      if (!String(newPolicy.effectiveDate || "").trim()) {
        return "Effective date is required before continuing.";
      }
    }
    if (step === 3) {
      if (
        newPolicy.approvalMethod === "workflow" &&
        (!newPolicy.workflowSteps || newPolicy.workflowSteps.length === 0)
      ) {
        return "Add at least one workflow step for 'Through workflow'.";
      }
      if (newPolicy.approvalMethod === "workflow") {
        const steps = Array.isArray(newPolicy.workflowSteps) ? newPolicy.workflowSteps : [];
        const hasApprovalStep = steps.some((s) => s?.role && !s.notifyOnly);
        if (steps.length > 0 && !hasApprovalStep) {
          return "Uncheck 'Notify only' on at least one step so Reporting Manager (or another role) must Approve — otherwise requests auto-complete with FYI only.";
        }
      }
    }
    if (step === 4) {
      if (newPolicy.autoLetter === "yes" && !String(newPolicy.letterFormat || "").trim()) {
        return "Choose a letter template when auto onboarding letter is enabled.";
      }
      if (newPolicy.useCancellationLetter && !String(newPolicy.cancellationLetterFormat || "").trim()) {
        return "Choose a cancellation letter template.";
      }
    }
    if (step === 5) {
      const incomplete = (newPolicy.checklistMappings || []).find((item) => !String(item.name || "").trim());
      if (incomplete) {
        return "Each checklist item needs a name.";
      }
    }
    if (step === 6) {
      const hasScope =
        !!newPolicy.allEmployees ||
        ORG_APPLICABILITY_KEYS.some((k) => newPolicy[k]) ||
        String(newPolicy.individualEmployeeIds || "").trim();
      if (!hasScope) {
        return "Select applicability scope (all employees, org filters, or individual employee IDs).";
      }
    }
    return null;
  };

  const goToNextPolicyStep = () => {
    const err = validatePolicyFormStep(policyFormStep);
    if (err) {
      setPolicySaveError(err);
      return;
    }
    setPolicySaveError(null);
    if (policyFormStep < POLICY_FORM_STEPS.length) {
      setPolicyFormStep((s) => s + 1);
    }
  };

  const goToPreviousPolicyStep = () => {
    setPolicySaveError(null);
    if (policyFormStep > 1) {
      setPolicyFormStep((s) => s - 1);
    }
  };

  const handleDeactivatePolicy = async (policyId) => {
    try {
      const res = await onboardingPolicyService.deactivateOnboardingPolicy(policyId);
      if (res.success) {
        toast.success("Policy deactivated");
        onPolicyChange?.();
      } else {
        toast.error(res.message || "Failed to deactivate policy");
      }
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Failed to deactivate policy");
    }
  };

  const handleReactivatePolicy = async (policyId) => {
    const policy = policies.find((p) => p.id === policyId);
    if (!policy) return;
    try {
      const res = await onboardingPolicyService.updateOnboardingPolicy(policyId, {
        ...mergePayloadForApi(normalizePolicyRow(policy)),
        status: "active",
      });
      if (res.success) {
        toast.success("Policy reactivated");
        onPolicyChange?.();
      } else {
        toast.error(res.message || "Failed to reactivate policy");
      }
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Failed to reactivate policy");
    }
  };

  const handleDeletePolicy = async (policyId) => {
    const policy = policies.find((p) => p.id === policyId);
    const policyName = policy?.policyName || policy?.name || "";
    await startPolicyDeleteFlow({
      policyId,
      policyName,
      getDeleteImpact: onboardingPolicyService.getOnboardingPolicyDeleteImpact,
      setDeleteModal: setDeletePolicyModal,
      onError: (message) => toast.error(message),
    });
  };

  const handleConfirmDeletePolicy = async () => {
    if (!deletePolicyModal?.policyId) return;
    await confirmPolicyDelete({
      policyId: deletePolicyModal.policyId,
      deletePolicy: onboardingPolicyService.deleteOnboardingPolicy,
      setDeleting: setDeletePolicyLoading,
      setDeleteModal: setDeletePolicyModal,
      onSuccess: () => {
        toast.success("Policy deleted");
        onPolicyChange?.();
      },
      onError: (message) => toast.error(message),
    });
  };

  const handleCopyPolicy = async (policy) => {
    const p = normalizePolicyRow(policy);
    const now = new Date().toISOString();
    const nm = `${p.policyName || p.name || "Policy"} (Copy)`;
    const eff = p.effectiveDate || new Date().toISOString().slice(0, 10);
    const copied = {
      ...createDefaultPolicy(),
      ...p,
      policyName: nm,
      name: nm,
      effectiveDate: eff,
      status: "active",
      createdAt: now,
      updatedAt: now,
      policyDocumentPushed: false,
      policyPushedAt: null,
      policyVersion: 1,
      policyVersionHistory: [
        {
          version: 1,
          effectiveDate: eff,
          savedAt: now,
          event: "created",
          policyName: nm,
        },
      ],
    };
    const snapBody = clonePolicySnapshot({
      ...copied,
      policySnapshots: undefined,
      policyVersion: 1,
    });
    copied.policySnapshots = [{ version: 1, effectiveDate: eff, savedAt: now, policy: snapBody }];
    try {
      const res = await onboardingPolicyService.createOnboardingPolicy(
        mergePayloadForApi(copied)
      );
      if (res.success) {
        toast.success("Policy copied");
        onPolicyChange?.();
      } else {
        toast.error(res.message || "Copy failed");
      }
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Copy failed");
    }
  };

  const handlePushPolicyDocument = async (policy) => {
    const now = new Date().toISOString();
    try {
      const res = await onboardingPolicyService.updateOnboardingPolicy(policy.id, {
        ...mergePayloadForApi(normalizePolicyRow(policy)),
        policyDocumentPushed: true,
        policyPushedAt: now,
      });
      if (res.success) {
        setPolicyDocModal((m) =>
          m && m.id === policy.id ? { ...m, policyDocumentPushed: true, policyPushedAt: now } : m
        );
        setActionMenu(null);
        toast.success("Policy document published to assigned employees.");
        onPolicyChange?.();
      } else {
        toast.error(res.message || "Failed to publish policy document");
      }
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Failed to publish policy document");
    }
  };

  const openPolicyDocumentPreview = (policy) => {
    const latest = policies.find((p) => p.id === policy.id) || policy;
    setPolicyDocModal(latest);
    setActionMenu(null);
  };

  const printPolicyFullDocument = (policy) => {
    const html = buildOnboardingPolicyDocumentHtml(policy, policyDocumentOptions);
    printOnboardingPolicyDocumentHtml(html);
  };

  const printPolicySnapshotDocument = (snapshotPolicy) => {
    const html = buildOnboardingPolicyDocumentHtml(snapshotPolicy, policyDocumentOptions);
    printOnboardingPolicyDocumentHtml(html);
  };

  const openActionMenuFromEvent = (e, policy) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const menuWidth = 220;
    const left = Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8));
    const top = rect.bottom + 4;
    setActionMenu((prev) => (prev?.policy?.id === policy.id ? null : { policy, top, left }));
  };

  const addWorkflowStep = (key) => {
    setNewPolicy((prev) => ({
      ...prev,
      [key]: [...(prev[key] || []), { role: defaultPolicyRoleValue || "MANAGER", slaDays: 3, notifyOnly: false }],
    }));
  };
  const removeWorkflowStep = (key, index) => {
    setNewPolicy((prev) => ({
      ...prev,
      [key]: (prev[key] || []).filter((_, i) => i !== index),
    }));
  };
  const updateWorkflowStep = (key, index, field, value) => {
    setNewPolicy((prev) => ({
      ...prev,
      [key]: (prev[key] || []).map((step, i) => (i === index ? { ...step, [field]: value } : step)),
    }));
  };
  const addChecklistItem = () => {
    setNewPolicy((prev) => ({
      ...prev,
      checklistMappings: [...(prev.checklistMappings || []), normalizeChecklistItem({})],
    }));
  };
  const removeChecklistItem = (index) => {
    setNewPolicy((prev) => ({
      ...prev,
      checklistMappings: (prev.checklistMappings || []).filter((_, i) => i !== index),
    }));
  };
  const updateChecklistItem = (index, field, value) => {
    setNewPolicy((prev) => ({
      ...prev,
      checklistMappings: (prev.checklistMappings || []).map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const toggleApprovalRole = (roleValue) => {
    setApprovalRolesForPolicy((prev) =>
      prev.includes(roleValue)
        ? prev.filter((r) => r !== roleValue)
        : [...prev, roleValue]
    );
  };

  const POLICY_SIGNATURE_OMIT = new Set([
    "id",
    "createdAt",
    "updatedAt",
    "policySnapshots",
    "policyVersionHistory",
    "policyDocumentPushed",
    "policyPushedAt",
    "policyVersion",
    "effectiveDate",
    "status",
  ]);

  const policyContentSignature = (p) => {
    if (!p) return "";
    const o = {};
    Object.keys(p)
      .sort()
      .forEach((k) => {
        if (POLICY_SIGNATURE_OMIT.has(k)) return;
        let v = p[k];
        if (k === "employmentTypes") v = Array.isArray(v) ? [...v].map(String).sort() : [];
        o[k] = v;
      });
    return JSON.stringify(o);
  };

  const parseIndividualIds = (policy) => {
    const s = policy.individualEmployeeIds;
    if (Array.isArray(s)) return s.map(String).filter(Boolean);
    return String(s || "")
      .split(/[\s,]+/)
      .map((x) => x.trim())
      .filter(Boolean);
  };

  const normalizeEmploymentTypesArr = (policy) => {
    const a = policy.employmentTypes;
    if (!Array.isArray(a) || a.length === 0) return null;
    return [...new Set(a.map(String))].sort();
  };

  const scopesApplicabilityOverlap = (a, b) => {
    if (String(a.id) === String(b.id)) return false;

    const aIds = parseIndividualIds(a);
    const bIds = parseIndividualIds(b);
    const aAll = !!a.allEmployees;
    const bAll = !!b.allEmployees;
    const aHasOrg = ORG_APPLICABILITY_KEYS.some((k) => a[k]);
    const bHasOrg = ORG_APPLICABILITY_KEYS.some((k) => b[k]);

    if (!aAll && !bAll && !aHasOrg && !bHasOrg && aIds.length && bIds.length) {
      const setB = new Set(bIds);
      return aIds.some((id) => setB.has(id));
    }

    if (aAll || bAll) {
      const aEm = normalizeEmploymentTypesArr(a);
      const bEm = normalizeEmploymentTypesArr(b);
      if (aEm && bEm) {
        const inter = aEm.filter((t) => bEm.includes(t));
        if (inter.length === 0) return false;
      }
      return true;
    }

    for (const k of ORG_APPLICABILITY_KEYS) {
      const va = a[k] ? String(a[k]) : "";
      const vb = b[k] ? String(b[k]) : "";
      if (va && vb && va !== vb) return false;
    }

    const aEm = normalizeEmploymentTypesArr(a);
    const bEm = normalizeEmploymentTypesArr(b);
    if (aEm && bEm) {
      const inter = aEm.filter((t) => bEm.includes(t));
      if (inter.length === 0) return false;
    }

    if (aIds.length && bIds.length) {
      const setB = new Set(bIds);
      return aIds.some((id) => setB.has(id));
    }
    if ((aIds.length && (bHasOrg || bAll)) || (bIds.length && (aHasOrg || aAll))) return true;
    if (aIds.length || bIds.length) return true;

    return true;
  };

  const findConflictingPolicies = (candidate, list, excludeId) =>
    list.filter((p) => {
      if (p.status === "inactive") return false;
      if (excludeId && String(p.id) === String(excludeId)) return false;
      return scopesApplicabilityOverlap(candidate, p);
    });

  const masterName = (list, id) => {
    if (!id) return "";
    return (list || []).find((x) => String(x.id) === String(id))?.name || "";
  };

  const summarizeOverlapReason = (candidate, other) => {
    const bits = [];
    if (candidate.allEmployees || other.allEmployees) {
      bits.push(
        'One policy is set to "All employees" (or the whole organization). That scope overlaps with any other rule that still applies to those people — for example a department-only policy.'
      );
    }
    for (const k of ORG_APPLICABILITY_KEYS) {
      const va = candidate[k] ? String(candidate[k]) : "";
      const vb = other[k] ? String(other[k]) : "";
      if (va && vb && va === vb) {
        const label =
          k === "branch"
            ? "Branch"
            : k === "department"
            ? "Department"
            : k === "designation"
            ? "Designation"
            : k === "grade"
            ? "Grade"
            : k === "category"
            ? "Category"
            : k === "subDepartment"
            ? "Sub-department"
            : "Location";
        const list =
          k === "branch"
            ? branchesList
            : k === "department"
            ? departmentsList
            : k === "designation"
            ? designationsList
            : k === "grade"
            ? gradesList
            : k === "category"
            ? categoriesList
            : k === "subDepartment"
            ? subDepartmentsList
            : null;
        const nm = list ? masterName(list, va) : va;
        bits.push(`Same ${label}: "${nm || va}".`);
      }
    }
    const aEm = normalizeEmploymentTypesArr(candidate);
    const bEm = normalizeEmploymentTypesArr(other);
    if (aEm && bEm) {
      const inter = aEm.filter((t) => bEm.includes(t));
      if (inter.length) {
        bits.push(
          `Overlapping employment type(s): ${inter
            .map((v) => EMPLOYMENT_TYPE_OPTIONS.find((o) => o.value === v)?.label || v)
            .join(", ")}.`
        );
      }
    } else if ((aEm || bEm) && (candidate.allEmployees || other.allEmployees)) {
      bits.push("Employment-type filters on one policy may still overlap with a broad 'all employees' policy.");
    }
    if (!bits.length) {
      bits.push(
        "These applicability rules may assign the same employees to more than one active onboarding policy. Saving will be final for this record."
      );
    }
    return bits.join(" ");
  };

  const toggleEmploymentType = (value) => {
    setNewPolicy((prev) => {
      const cur = Array.isArray(prev.employmentTypes) ? prev.employmentTypes : [];
      const has = cur.includes(value);
      return {
        ...prev,
        employmentTypes: has ? cur.filter((v) => v !== value) : [...cur, value],
      };
    });
  };

  /** HR policy print / preview rows (onboarding). */
  const getPolicySummaryRows = (p) => {
    const indIds = Array.isArray(p.individualEmployeeIds)
      ? p.individualEmployeeIds.join(", ")
      : p.individualEmployeeIds || "";
    const appLabel = (list, value) => {
      if (!value) return "All";
      const found = (list || []).find((x) => String(x.id) === String(value));
      return found?.name || (value ? "—" : "All");
    };
    const wfSteps = Array.isArray(p.workflowSteps) ? p.workflowSteps : [];
    const cancelSteps = Array.isArray(p.cancellationWorkflowSteps) ? p.cancellationWorkflowSteps : [];
    const checklist = Array.isArray(p.checklistMappings) ? p.checklistMappings : [];

    const rows = [];
    rows.push({
      label: "Policy name",
      value: typeof p.policyName === "string" ? p.policyName.trim() || p.policyName : p.policyName || "—",
    });
    rows.push({ label: "Effective date", value: p.effectiveDate || "—" });
    rows.push({
      label: "Policy document version",
      value: p.policyVersion != null ? String(p.policyVersion) : "1",
    });
    rows.push({
      label: "Auto-assign this policy on joining",
      value: p.autoAssignOnJoin ? "Yes" : "No",
    });
    rows.push({
      label: "Approval method",
      value:
        p.approvalMethod === "workflow" ? "Through workflow" : "Auto approved",
    });
    if (p.approvalMethod === "workflow") {
      rows.push({ label: "Reminder days before joining date", value: p.reminderDays ?? "—" });
    }
    rows.push({ label: "Auto onboarding letter", value: p.autoLetter === "yes" ? "Yes" : "No" });
    if (p.autoLetter === "yes") {
      rows.push({
        label: "Onboarding letter template",
        value: getAppointmentLetterLabel(p.letterFormat),
      });
    }
    rows.push({
      label: "Cancellation letter assignment",
      value: p.useCancellationLetter ? "Yes" : "No",
    });
    if (p.useCancellationLetter) {
      rows.push({
        label: "Cancellation letter template",
        value:
          getAppointmentLetterLabel(p.cancellationLetterFormat),
      });
    }
    rows.push({ label: "Parallel approval option", value: p.workflowParallelApproval ? "Yes" : "No" });
    rows.push({ label: "Escalation after (days)", value: p.workflowEscalationAfterDays ?? "—" });
    rows.push({
      label: "Auto-approval if no action",
      value: p.workflowAutoApprovalIfNoAction ? "Yes" : "No",
    });
    rows.push({
      label: "Rejection with reason",
      value: p.workflowRejectionWithReason ? "Yes" : "No",
    });
    rows.push({
      label: "Re-initiation allowed",
      value: p.workflowReinitiationAllowed ? "Yes" : "No",
    });
    rows.push({
      label: "Block joining without profile completion",
      value: p.blockJoiningWithoutProfileCompletion !== false ? "Yes" : "No",
    });
    rows.push({
      label: "Mandatory profile tabs (for approval)",
      value:
        p.blockJoiningWithoutProfileCompletion === false
          ? "None (gate off)"
          : Array.isArray(p.mandatoryProfileTabs) && p.mandatoryProfileTabs.length
            ? p.mandatoryProfileTabs.join(", ")
            : "All tabs",
    });
    if (wfSteps.length) {
      wfSteps.forEach((s, i) =>
        rows.push({
          label: `Step ${i + 1} (Onboarding approval)`,
          value: `${getPolicyRoleLabel(s.role)}${
            s.notifyOnly ? " (Notify only)" : ` — SLA ${s.slaDays ?? 3} days`
          }`,
        })
      );
    } else {
      rows.push({ label: "Workflow steps (Onboarding approval)", value: "—" });
    }
    if (cancelSteps.length) {
      cancelSteps.forEach((s, i) =>
        rows.push({
          label: `Step ${i + 1} (Cancellation)`,
          value: `${getPolicyRoleLabel(s.role)}${
            s.notifyOnly ? " (Notify only)" : ` — SLA ${s.slaDays ?? 3} days`
          }`,
        })
      );
    }
    if (checklist.length) {
      checklist.forEach((item, i) =>
        rows.push({
          label: `Checklist item ${i + 1}`,
          value: `Trigger: ${item.triggerEvent || "—"} | Dept: ${item.department || "—"} | ${item.name || "—"} | ${
            item.mandatory ? "Mandatory" : "Optional"
          } | Due: ${item.dueDateLogic || "—"}${item.dueDateDays ? ` + ${item.dueDateDays} days` : ""}`,
        })
      );
    } else {
      rows.push({ label: "Checklist mapping", value: "None listed" });
    }
    rows.push({
      label: "All employees (applicability)",
      value: p.allEmployees ? "Yes" : "No",
    });
    rows.push({
      label: "Employment types (applicability)",
      value:
        Array.isArray(p.employmentTypes) && p.employmentTypes.length
          ? p.employmentTypes
              .map((v) => EMPLOYMENT_TYPE_OPTIONS.find((o) => o.value === v)?.label || v)
              .join(", ")
          : "All types",
    });
    rows.push({ label: "Branch (applicability)", value: appLabel(branchesList, p.branch) });
    rows.push({ label: "Department (applicability)", value: appLabel(departmentsList, p.department) });
    rows.push({ label: "Designation (applicability)", value: appLabel(designationsList, p.designation) });
    rows.push({ label: "Grade (applicability)", value: appLabel(gradesList, p.grade) });
    rows.push({ label: "Category (applicability)", value: appLabel(categoriesList, p.category) });
    rows.push({
      label: "Sub-department / Division (applicability)",
      value: appLabel(subDepartmentsList, p.subDepartment),
    });
    rows.push({
      label: "Location (applicability)",
      value: LOCATION_OPTIONS.find((o) => o.value === (p.location || ""))?.label || (p.location ? "—" : "All"),
    });
    rows.push({
      label: "Individual employees (override)",
      value: (typeof indIds === "string" && indIds.trim()) || "—",
    });
    return rows;
  };

  const getApprovalLevelsCount = (policy) => {
    if (policy.approvalMethod === "workflow" && Array.isArray(policy.workflowSteps)) {
      return (policy.workflowSteps || []).length;
    }
    return "—";
  };

  const printPolicyDocument = (policy) => {
    printSinglePolicyAsDocument(
      "Onboarding Policy",
      policy.policyName,
      policy.effectiveDate,
      getPolicySummaryRows,
      policy
    );
  };

  const getDynamicPolicyGuideLines = (policy) => {
    const p = policy || createDefaultPolicy();
    const wfSteps = Array.isArray(p.workflowSteps) ? p.workflowSteps.length : 0;
    const cancelSteps = Array.isArray(p.cancellationWorkflowSteps) ? p.cancellationWorkflowSteps.length : 0;
    const checklistCount = Array.isArray(p.checklistMappings) ? p.checklistMappings.length : 0;
    const profileTabs =
      p.blockJoiningWithoutProfileCompletion === false
        ? 0
        : Array.isArray(p.mandatoryProfileTabs) && p.mandatoryProfileTabs.length
          ? p.mandatoryProfileTabs.length
          : PROFILE_TAB_OPTIONS.length;

    return [
      `This policy uses "${p.approvalMethod === "workflow" ? "Through workflow" : "Auto approved"}" approval mode.`,
      `Reminder before joining (days): ${p.approvalMethod === "workflow" ? p.reminderDays ?? "—" : "—"}.`,
      `Workflow steps: onboarding approval ${wfSteps}, cancellation ${cancelSteps}.`,
      `Mandatory profile tabs for approval: ${profileTabs}. Checklist mapping rows: ${checklistCount}.`,
      `Applicability scope: ${p.allEmployees ? "All employees" : "Filtered by selected organization fields"}.`,
    ];
  };

  const escapeXml = (s) => {
    if (s == null) return "";
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  };

  /** Onboarding: case → reviews → decision → complete / reject / cancel */
  const buildOnboardingProcessSvg = (policy) => {
    const p = policy || {};
    const wf = Array.isArray(p.workflowSteps) ? p.workflowSteps : [];
    const cancelWf = Array.isArray(p.cancellationWorkflowSteps) ? p.cancellationWorkflowSteps : [];
    const chkCount = Array.isArray(p.checklistMappings) ? p.checklistMappings.length : 0;
    const autoLetter = p.autoLetter === "yes";
    const reinit = p.workflowReinitiationAllowed !== false;

    const cx = 460;
    let y = 32;
    const parts = [];
    const pushLine = (y0, y1) => {
      parts.push(
        `<line x1="${cx}" y1="${y0}" x2="${cx}" y2="${y1}" stroke="#64748b" stroke-width="2" marker-end="url(#onb-arr)"/>`
      );
    };

    parts.push(
      `<text x="${cx}" y="${y}" text-anchor="middle" font-size="15" font-weight="700" fill="#0f172a">Onboarding process</text>`
    );
    y += 24;
    parts.push(
      `<text x="${cx}" y="${y}" text-anchor="middle" font-size="10" fill="#64748b">New joiner case → document checks → approvals → outcome.</text>`
    );
    y += 28;

    parts.push(`<circle cx="${cx}" cy="${y}" r="14" stroke="#64748b" fill="#fff" stroke-width="2"/>`);
    parts.push(`<text x="${cx}" y="${y + 4}" text-anchor="middle" font-size="9" fill="#334155">START</text>`);
    y += 22;
    pushLine(y - 8, y);
    const reqH = 44;
    parts.push(
      `<rect x="${cx - 120}" y="${y}" width="240" height="${reqH}" rx="8" fill="#fff" stroke="#334155" stroke-width="1.5"/>`
    );
    parts.push(
      `<text x="${cx}" y="${y + 20}" text-anchor="middle" font-size="11" font-weight="700" fill="#0f172a">Onboarding case</text>`
    );
    parts.push(
      `<text x="${cx}" y="${y + 36}" text-anchor="middle" font-size="9" fill="#475569">Submit documents · HR opens case</text>`
    );
    y += reqH + 14;

    if (wf.length) {
      wf.forEach((s, i) => {
        pushLine(y - 14, y);
        const role = getPolicyRoleLabel(s.role);
        const sub = s.notifyOnly ? "Notify only" : `Action / SLA ${s.slaDays || 3}d`;
        parts.push(
          `<rect x="${cx - 115}" y="${y}" width="230" height="40" rx="6" fill="#f8fafc" stroke="#64748b" stroke-width="1.2"/>`
        );
        parts.push(
          `<text x="${cx}" y="${y + 16}" text-anchor="middle" font-size="10" font-weight="600" fill="#0f172a">Step ${i + 1}: ${escapeXml(
            role
          )}</text>`
        );
        parts.push(
          `<text x="${cx}" y="${y + 32}" text-anchor="middle" font-size="9" fill="#475569">${escapeXml(sub)}</text>`
        );
        y += 54;
      });
    } else {
      pushLine(y - 14, y);
      parts.push(
        `<rect x="${cx - 115}" y="${y}" width="230" height="38" rx="6" fill="#fefce8" stroke="#ca8a04" stroke-width="1.2"/>`
      );
      parts.push(
        `<text x="${cx}" y="${y + 24}" text-anchor="middle" font-size="9" fill="#713f12">No approval steps — routing follows policy method</text>`
      );
      y += 52;
    }

    pushLine(y - 14, y);
    const d = 26;
    const decisionBottom = y + 2 * d;
    parts.push(
      `<polygon points="${cx},${y} ${cx + d},${y + d} ${cx},${y + 2 * d} ${cx - d},${y + d}" fill="#fff" stroke="#0f172a" stroke-width="2"/>`
    );
    parts.push(
      `<text x="${cx}" y="${y + d + 4}" text-anchor="middle" font-size="8" font-weight="700" fill="#0f172a">Decision</text>`
    );
    parts.push(
      `<text x="${cx}" y="${y + 2 * d - 4}" text-anchor="middle" font-size="7" fill="#64748b">Complete · Reject · Cancel</text>`
    );
    y = decisionBottom + 20;
    parts.push(
      `<text x="${cx}" y="${y}" text-anchor="middle" font-size="9" font-weight="600" fill="#475569">Outcomes</text>`
    );
    y += 22;

    const completeLines = [
      wf.length ? `Finish approvals (${wf.length} step(s))` : "Complete per approval method",
      autoLetter ? "Welcome / onboarding letter" : "Communications as per HR",
      `Tasks & checklist (${chkCount} mapped row(s))`,
      "END: Onboarding complete",
    ];
    const rejectLines = [
      "Request not approved",
      p.workflowRejectionWithReason !== false ? "Reason recorded" : "Reason optional",
      reinit ? "May re-open or re-submit per HR" : "Case closed",
      "END: Rejected",
    ];
    const cancelLines = [
      cancelWf.length ? `Cancellation path (${cancelWf.length} step(s))` : "Cancel per HR / joiner request",
      "Notifications to stakeholders",
      "END: Case withdrawn",
    ];

    const outcomeDefs = [
      { title: "Complete", color: "#16a34a", lines: completeLines },
      { title: "Reject", color: "#dc2626", lines: rejectLines },
      { title: "Cancel", color: "#64748b", lines: cancelLines },
    ];

    const boxW = 240;
    const gap = 24;
    const lineH = 12;
    const padTop = 22;
    const heights = outcomeDefs.map((o) => padTop + o.lines.length * lineH + 10);
    const boxH = Math.max(...heights);
    const totalW = 3 * boxW + 2 * gap;
    const startX = cx - totalW / 2;
    const rowTop = y;
    const centers = [0, 1, 2].map((i) => startX + i * (boxW + gap) + boxW / 2);
    const colors = ["#16a34a", "#dc2626", "#64748b"];
    centers.forEach((bx, i) => {
      parts.push(
        `<path d="M ${cx} ${decisionBottom} L ${bx} ${rowTop - 2}" fill="none" stroke="${colors[i]}" stroke-width="1.4" marker-end="url(#onb-arr)"/>`
      );
    });

    outcomeDefs.forEach((o, i) => {
      const x = startX + i * (boxW + gap);
      parts.push(
        `<rect x="${x}" y="${rowTop}" width="${boxW}" height="${boxH}" rx="8" fill="#fafafa" stroke="${o.color}" stroke-width="2"/>`
      );
      parts.push(
        `<text x="${x + 10}" y="${rowTop + 16}" font-size="10" font-weight="700" fill="${o.color}">${escapeXml(o.title)}</text>`
      );
      o.lines.forEach((ln, j) => {
        parts.push(
          `<text x="${x + 10}" y="${rowTop + padTop + j * lineH}" font-size="8.5" fill="#334155">${escapeXml(ln)}</text>`
        );
      });
    });

    const H = rowTop + boxH + 36;
    const W = 880;
    return `
      <svg width="${W}" height="${H}" viewBox="0 0 ${W} ${H}" style="max-width:100%;height:auto">
        <defs>
          <marker id="onb-arr" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
            <polygon points="0 0, 8 4, 0 8" fill="#64748b"/>
          </marker>
        </defs>
        ${parts.join("")}
      </svg>
    `;
  };

  const printCombinedFlowchartPdf = (policy) => {
    const html = `
      <html>
      <head>
        <title>Onboarding process — ${policy?.policyName || "Policy"}</title>
        <style>
          body { font-family: "Segoe UI", Inter, Arial, sans-serif; margin: 20px; color: #0f172a; }
          h2 { margin: 0 0 12px 0; font-size: 16px; }
          .hint { font-size: 12px; color: #475569; margin-bottom: 14px; }
          .flow { border: 1px solid #e2e8f0; padding: 12px; border-radius: 8px; page-break-inside: avoid; }
          @media print { .flow { break-inside: avoid; } body { margin: 12px; } }
        </style>
      </head>
      <body>
        <h2>Onboarding process — ${policy?.policyName || "Policy"}</h2>
        <div class="hint">End-to-end onboarding flow (print or Save as PDF from the dialog).</div>
        <div class="flow">${buildOnboardingProcessSvg(policy || {})}</div>
      </body>
      </html>
    `;
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
  };

  const printAllPolicies = () => {
    printAllPoliciesAsDocument("Onboarding Policy", policies, (p) => getPolicySummaryRows(p));
  };

  const getApplicabilityBadges = (policy) => {
    const badges = [];
    if (policy.allEmployees) badges.push("All employees");
    if (!policy.allEmployees) {
      const addBadge = (list, key, labelPrefix) => {
        if (!policy[key]) return;
        const item = (list || []).find((x) => String(x.id) === String(policy[key]));
        badges.push(`${labelPrefix}: ${item?.name || policy[key]}`);
      };
      addBadge(branchesList, "branch", "Branch");
      addBadge(departmentsList, "department", "Dept");
      addBadge(designationsList, "designation", "Designation");
      addBadge(gradesList, "grade", "Grade");
      addBadge(categoriesList, "category", "Category");
      addBadge(subDepartmentsList, "subDepartment", "Sub Dept");
    }
    if (Array.isArray(policy.employmentTypes) && policy.employmentTypes.length) {
      const labels = policy.employmentTypes.map(
        (v) => EMPLOYMENT_TYPE_OPTIONS.find((o) => o.value === v)?.label || v
      );
      badges.push(`Employment: ${labels.join(", ")}`);
    }
    return badges.length ? badges : ["No scope"];
  };

  const visiblePolicies = policies
    .filter((p) => (showInactivePolicies ? p.status === "inactive" : p.status === "active"))
    .filter((p) =>
      String(p.policyName || p.name || "")
        .toLowerCase()
        .includes(String(policySearch || "").toLowerCase().trim())
    );
  const listedPolicies = visiblePolicies.slice(0, 10);
  const selectedApprovalRoleLabels = useMemo(
    () =>
      APPROVAL_ROLES.filter((r) => approvalRolesForPolicy.includes(r.value)).map(
        (r) => r.label
      ),
    [approvalRolesForPolicy]
  );

  return (
    <div className="flex h-full min-h-0 flex-col bg-[hsl(var(--ds-surface))] p-3 text-[hsl(var(--ds-fg))] sm:p-4">
      <PolicyListToolbar
        title="Onboarding Policies"
        newLabel="+ New Onboarding Policy"
        onNew={openCreateForm}
        onPrint={printAllPolicies}
        searchValue={policySearch}
        onSearchChange={(e) => setPolicySearch(e.target.value)}
        showInactive={showInactivePolicies}
        onToggleInactive={() => setShowInactivePolicies((v) => !v)}
        rolesSlot={
          <div className="relative" ref={approvalSettingsRef}>
            <DsButton
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setApprovalSettingsOpen((v) => !v)}
              title="Roles that can approve policies"
            >
              Roles
            </DsButton>
            {approvalSettingsOpen && (
              <div className="absolute right-0 z-20 mt-2 w-64 rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-2 shadow-[var(--ds-shadow-card)]">
                <div className="mb-2 border-b border-[hsl(var(--ds-border))] px-2 py-1 text-xs font-semibold uppercase text-[hsl(var(--ds-muted-fg))]">
                  Roles that can approve policy
                </div>
                {APPROVAL_ROLES.map((role) => (
                  <label key={role.value} className="flex items-center gap-2 px-2 py-1 text-sm">
                    <input
                      type="checkbox"
                      checked={approvalRolesForPolicy.includes(role.value)}
                      onChange={() => toggleApprovalRole(role.value)}
                    />
                    {role.label}
                  </label>
                ))}
              </div>
            )}
          </div>
        }
      />

      <div className="ds-table-wrap min-h-0 flex-1">
        {loadingPolicies ? (
          <div className="p-8 text-center text-sm text-[hsl(var(--ds-muted-fg))]">Loading onboarding policies...</div>
        ) : (
        <div className="max-h-[62vh] overflow-y-auto">
          <table className="ds-table w-full text-left">
            <PolicyListTableHeader ruleColumnLabel="Profile tabs" />
            <tbody className="divide-y divide-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]">
              {listedPolicies.map((policy, idx) => {
                const badges = getApplicabilityBadges(policy);
                return (
                  <tr key={policy.id} className="hover:bg-[hsl(var(--ds-muted))]/50 transition-colors">
                    <td className="px-4 py-3 text-[hsl(var(--ds-muted-fg))]">{idx + 1}</td>
                    <td className="px-4 py-3 font-medium text-[hsl(var(--ds-fg))]">
                      {policy.policyName || policy.name}
                    </td>
                    <td className="px-4 py-3 text-center text-[hsl(var(--ds-fg))] tabular-nums">
                      v{policy.policyVersion ?? 1}
                    </td>
                    <td className="px-4 py-3 text-[hsl(var(--ds-fg))]">
                      {policy.blockJoiningWithoutProfileCompletion === false
                        ? "Off"
                        : `${
                            Array.isArray(policy.mandatoryProfileTabs) &&
                            policy.mandatoryProfileTabs.length
                              ? policy.mandatoryProfileTabs.length
                              : PROFILE_TAB_OPTIONS.length
                          } req.`}
                    </td>
                    <td className="px-4 py-3 text-[hsl(var(--ds-fg))]">{policy.reminderDays ?? 7}d</td>
                    <td className="px-4 py-3 text-[hsl(var(--ds-fg))]">
                      {policy.approvalMethod === "workflow"
                        ? "Workflow"
                        : "Auto approved"}
                    </td>
                    <td className="px-4 py-3 text-center text-[hsl(var(--ds-fg))]">
                      {getApprovalLevelsCount(policy)}
                    </td>
                    <td className="px-4 py-3 text-[hsl(var(--ds-fg))]">
                      {policy.autoLetter === "yes" ? "Yes" : "No"}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <PolicyAppliedMastersEye
                        policyName={policy.policyName || policy.name}
                        badges={badges}
                        policyDocumentPushed={!!policy.policyDocumentPushed}
                        darkMode={darkMode}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <span className={policyListStatusClass(policy.status === "active")}>
                        {policy.status || "active"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        type="button"
                        aria-label="Row actions"
                        className={policyListRowActionClass}
                        onClick={(e) => openActionMenuFromEvent(e, policy)}
                      >
                        <MoreVertical size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
      </div>
      {listedPolicies.length === 0 && (
        <p className={`text-sm py-6 text-center ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
          {policySearch.trim()
            ? "No policies match your search."
            : 'No policies. Click "New Onboarding Policy" to create one.'}
        </p>
      )}

      {isFormOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/45 backdrop-blur-[2px]">
          <div
            className={`w-[min(92vw,780px)] h-[min(90vh,720px)] rounded-2xl shadow-2xl border flex flex-col overflow-hidden ${
              darkMode
                ? "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))]"
                : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))]"
            }`}
          >
            <div
              className={`px-5 py-4 flex items-center justify-between flex-shrink-0 border-b ${
                darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
              }`}
            >
              <div>
                <h3 className={`text-[15px] font-semibold ${darkMode ? "text-[hsl(var(--ds-fg))]" : "text-[hsl(var(--ds-fg))]"}`}>
                  {isEditing ? "Edit onboarding policy" : "New onboarding policy"}
                </h3>
                <p className={`text-[11px] mt-0.5 ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
                  Complete all six steps to configure this onboarding policy.
                </p>
              </div>
              <div className="flex items-center gap-2 relative" ref={policyHelpRef}>
                <button
                  type="button"
                  onClick={() => setPolicyHelpOpen((v) => !v)}
                  className={darkMode ? "text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]" : "text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"}
                  title="Field guide"
                >
                  <Info size={18} />
                </button>
                <button
                  onClick={handleCancelPolicy}
                  className={darkMode ? "text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]" : "text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"}
                >
                  <X size={18} />
                </button>
                {policyHelpOpen && (
                  <div className="absolute right-0 top-8 w-[420px] max-w-[80vw] p-3 bg-[hsl(var(--ds-surface))] border border-[hsl(var(--ds-border))] shadow-lg rounded-xl z-20 text-xs text-[hsl(var(--ds-fg))] space-y-1">
                    {getDynamicPolicyGuideLines(newPolicy).map((line, idx) => (
                      <p key={`guide-${idx}`}>{line}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div
              className={`flex-1 min-h-0 flex flex-col px-5 py-3 text-[12px] leading-snug ${
                darkMode ? "bg-[hsl(var(--ds-muted)/0.35)]" : "bg-[hsl(var(--ds-muted)/0.35)]"
              }`}
            >
              <div
                className={`flex-shrink-0 p-3 border rounded-xl ${
                  darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted)/0.4)]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <p className={`text-xs font-semibold uppercase tracking-wide ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
                    Policy wizard
                  </p>
                  <p className={`text-[11px] ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
                    Step {policyFormStep} of {POLICY_FORM_STEPS.length}
                  </p>
                </div>
                <div className="flex items-center justify-between gap-1 overflow-x-auto pb-1">
                  {POLICY_FORM_STEPS.map((step, idx) => (
                    <div key={step.id} className="flex items-center flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => {
                          if (step.id < policyFormStep) {
                            setPolicySaveError(null);
                            setPolicyFormStep(step.id);
                          }
                        }}
                        disabled={step.id > policyFormStep}
                        className={`flex flex-col items-center min-w-[56px] ${
                          step.id <= policyFormStep ? "cursor-pointer" : "cursor-default"
                        }`}
                        title={step.label}
                      >
                        <span
                          className={`w-7 h-7 rounded-full border-2 flex items-center justify-center text-[11px] font-semibold ${
                            policyFormStep === step.id
                              ? "border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-accent))] text-[hsl(var(--ds-primary))]"
                              : policyFormStep > step.id
                                ? "border-[hsl(var(--ds-success))] bg-[hsl(var(--ds-success)/0.12)] text-[hsl(var(--ds-success))]"
                                : darkMode
                                  ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]"
                                  : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]"
                          }`}
                        >
                          {policyFormStep > step.id ? <Check size={14} /> : step.id}
                        </span>
                        <span
                          className={`mt-1 text-[9px] text-center leading-tight max-w-[64px] ${
                            policyFormStep === step.id
                              ? darkMode
                                ? "text-[hsl(var(--ds-fg))] font-medium"
                                : "text-[hsl(var(--ds-fg))] font-medium"
                              : darkMode
                                ? "text-[hsl(var(--ds-muted-fg))]"
                                : "text-[hsl(var(--ds-muted-fg))]"
                          }`}
                        >
                          {step.shortLabel}
                        </span>
                      </button>
                      {idx < POLICY_FORM_STEPS.length - 1 && (
                        <div
                          className={`w-4 sm:w-6 h-0.5 mx-0.5 mb-4 ${
                            policyFormStep > step.id
                              ? "bg-emerald-500"
                              : darkMode
                                ? "bg-[hsl(var(--ds-muted))]"
                                : "bg-[hsl(var(--ds-muted))]"
                          }`}
                        />
                      )}
                    </div>
                  ))}
                </div>
                <p className={`text-[11px] text-center mt-1 ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
                  Step {policyFormStep} of {POLICY_FORM_STEPS.length}:{" "}
                  <span className="font-medium">{POLICY_FORM_STEPS[policyFormStep - 1]?.label}</span>
                </p>
              </div>

              <div
                className={`mt-3 flex-1 min-h-[400px] max-h-[400px] overflow-y-auto p-4 border rounded-xl shadow-sm ${
                  darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted)/0.5)]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
                }`}
              >
              {policyFormStep === 1 && (
              <div>
                <h4 className={`text-xs font-semibold uppercase tracking-wide mb-3 ${darkMode ? "text-[hsl(var(--ds-fg))]" : "text-[hsl(var(--ds-fg))]"}`}>
                  Policy basics
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className={`block text-xs font-medium mb-0.5 ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>Policy Name</label>
                    <input
                      type="text"
                      value={newPolicy.policyName || ""}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, policyName: e.target.value }))}
                      className={`w-full py-1.5 px-2.5 border rounded-lg text-sm ${darkMode ? "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-white" : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"}`}
                      placeholder="e.g. Default Onboarding Policy"
                    />
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-0.5 ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>Effective Date</label>
                    <input
                      type="date"
                      value={newPolicy.effectiveDate || ""}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, effectiveDate: e.target.value }))}
                      className={`w-full py-1.5 px-2.5 border rounded-lg text-sm ${darkMode ? "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-white" : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"}`}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <p className={`text-[10px] mb-2 leading-snug ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
                      Policy document version increases automatically when you save with a new effective date that is after the
                      previous one and is a future date (today excluded). Current document version: v
                      {newPolicy.policyVersion ?? 1}.
                    </p>
                    <label className="flex items-center text-xs">
                      <input
                        type="checkbox"
                        checked={!!newPolicy.autoAssignOnJoin}
                        onChange={(e) => setNewPolicy((p) => ({ ...p, autoAssignOnJoin: e.target.checked }))}
                        className="mr-2"
                      />
                      Auto-assign this policy on joining
                    </label>
                  </div>
                </div>
              </div>
              )}

              {policyFormStep === 2 && (
              <div>
                <h4 className={`text-xs font-semibold uppercase tracking-wide mb-2 ${darkMode ? "text-[hsl(var(--ds-fg))]" : "text-[hsl(var(--ds-fg))]"}`}>
                  Profile completion for approval / joining
                </h4>
                <p className={`text-[11px] mb-3 ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
                  Choose which employee profile tabs must be complete before onboarding approval. Uncheck a tab to make it optional.
                </p>
                <label className="flex items-start gap-2 text-sm cursor-pointer mb-3">
                  <input
                    type="checkbox"
                    checked={newPolicy.blockJoiningWithoutProfileCompletion !== false}
                    onChange={(e) =>
                      setNewPolicy((p) => ({
                        ...p,
                        blockJoiningWithoutProfileCompletion: e.target.checked,
                      }))
                    }
                    className="mt-0.5 rounded border-[hsl(var(--ds-border))]"
                  />
                  <span className={darkMode ? "text-[hsl(var(--ds-fg))]" : "text-[hsl(var(--ds-fg))]"}>
                    Block joining / approval without profile completion
                    <span className={`block text-[11px] mt-0.5 ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
                      When enabled, Approve stays disabled until checked tabs below are complete.
                    </span>
                  </span>
                </label>
                {newPolicy.blockJoiningWithoutProfileCompletion !== false ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {PROFILE_TAB_OPTIONS.map((tab) => {
                      const selected = Array.isArray(newPolicy.mandatoryProfileTabs)
                        ? newPolicy.mandatoryProfileTabs.includes(tab)
                        : true;
                      return (
                        <label key={tab} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setNewPolicy((p) => {
                                const current = Array.isArray(p.mandatoryProfileTabs)
                                  ? [...p.mandatoryProfileTabs]
                                  : [...PROFILE_TAB_OPTIONS];
                                const next = checked
                                  ? Array.from(new Set([...current, tab]))
                                  : current.filter((t) => t !== tab);
                                return {
                                  ...p,
                                  mandatoryProfileTabs: next.length ? next : [tab],
                                };
                              });
                            }}
                            className="rounded border-[hsl(var(--ds-border))]"
                          />
                          <span className={darkMode ? "text-[hsl(var(--ds-fg))]" : "text-[hsl(var(--ds-fg))]"}>
                            {tab}
                            <span className={`ml-1 text-[10px] ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
                              (required for approval)
                            </span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <p className={`text-[11px] ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
                    Profile tabs are not required for approval when this option is off.
                  </p>
                )}
              </div>
              )}

              {policyFormStep === 3 && (
              <PolicyWorkflowStepSection
                darkMode={darkMode}
                policy={newPolicy}
                setPolicy={setNewPolicy}
                workflowStepsTab={workflowStepsTab}
                setWorkflowStepsTab={setWorkflowStepsTab}
                policyRoleOptions={policyRoleOptions}
                addWorkflowStep={addWorkflowStep}
                removeWorkflowStep={removeWorkflowStep}
                updateWorkflowStep={updateWorkflowStep}
                tabs={ONBOARDING_WORKFLOW_TABS}
                autoApprovedMessage="Onboarding requests under this policy are approved automatically once submitted — no reviewer chain is required."
                reminderDaysLabel="Reminder days before joining"
              />
              )}

              {policyFormStep === 4 && (
              <div>
                <h4 className={`text-sm font-semibold mb-2 ${darkMode ? "text-white" : "text-[hsl(var(--ds-fg))]"}`}>Communication &amp; letters</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className={`block text-sm mb-1 ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]"}`}>Auto onboarding letter</label>
                    <select value={newPolicy.autoLetter} onChange={(e) => setNewPolicy((p) => ({ ...p, autoLetter: e.target.value }))} className={`w-full p-2 border rounded-lg text-sm ${darkMode ? "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-white" : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"}`}>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm mb-1 ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]"}`}>Choose letter template</label>
                    <select value={newPolicy.letterFormat} onChange={(e) => setNewPolicy((p) => ({ ...p, letterFormat: e.target.value }))} className={`w-full p-2 border rounded-lg text-sm ${darkMode ? "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-white" : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"}`}>
                      {appointmentLetterOptions.map((o) => (
                        <option key={`f-${o.value}`} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="flex items-center gap-2 text-sm cursor-pointer mb-2">
                      <input
                        type="checkbox"
                        checked={!!newPolicy.useCancellationLetter}
                        onChange={(e) => setNewPolicy((p) => ({ ...p, useCancellationLetter: e.target.checked }))}
                        className="rounded border-[hsl(var(--ds-border))]"
                      />
                      <span className={darkMode ? "text-gray-200" : "text-[hsl(var(--ds-fg))]"}>Cancellation letter assignment</span>
                    </label>
                    {newPolicy.useCancellationLetter && (
                      <div>
                        <label className={`block text-sm mb-1 ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]"}`}>Cancellation letter template</label>
                        <select
                          value={newPolicy.cancellationLetterFormat || ""}
                          onChange={(e) => setNewPolicy((p) => ({ ...p, cancellationLetterFormat: e.target.value }))}
                          className={`w-full max-w-md p-2 border rounded-lg text-sm ${darkMode ? "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-white" : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"}`}
                        >
                          {appointmentLetterOptions.map((o) => (
                            <option key={`c-${o.value}`} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              )}

              {policyFormStep === 5 && (
              <PolicyChecklistStepSection
                darkMode={darkMode}
                policy={newPolicy}
                checklistMappings={newPolicy.checklistMappings || []}
                description="Additional tasks triggered on onboarding or approval events (e.g. IT access, ID card)."
                emptyHint="Add checklist items (e.g. Salary revision, Insurance enrollment, ID card, Asset allocation, Access upgrade, Clearance)."
                triggerEvents={CHECKLIST_TRIGGER_EVENTS}
                departments={CHECKLIST_DEPARTMENTS}
                dueDateLogicOptions={DUE_DATE_LOGIC_OPTIONS}
                policyRoleOptions={policyRoleOptions}
                addChecklistItem={addChecklistItem}
                removeChecklistItem={removeChecklistItem}
                updateChecklistItem={updateChecklistItem}
              />
              )}

              {policyFormStep === 6 && (
              <PolicyApplicabilityStepSection
                darkMode={darkMode}
                policy={newPolicy}
                setPolicy={setNewPolicy}
                branchesList={branchesList}
                departmentsList={departmentsList}
                designationsList={designationsList}
                gradesList={gradesList}
                categoriesList={categoriesList}
                subDepartmentsList={subDepartmentsList}
                locationOptions={LOCATION_OPTIONS}
                employmentTypeOptions={EMPLOYMENT_TYPE_OPTIONS}
                onToggleEmploymentType={toggleEmploymentType}
              >

              </PolicyApplicabilityStepSection>
              )}
              </div>
            </div>

            <div
              className={`px-5 py-4 border-t flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 flex-shrink-0 rounded-b-2xl ${
                darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]/80" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]"
              }`}
            >
              <div className="min-h-[1.25rem]">
                {policySaveError && <p className="text-sm text-red-600">{policySaveError}</p>}
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2">
                {policyFormStep === POLICY_FORM_STEPS.length && (
                  <button
                    type="button"
                    onClick={() => setFlowchartPolicy(newPolicy)}
                    className="inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-secondary))] px-4 py-2 text-sm font-semibold text-[hsl(var(--ds-secondary-fg))] hover:opacity-90 shadow-sm"
                    title="Open preview; use Print / Save as PDF inside the preview"
                  >
                    Preview Flowchart
                  </button>
                )}
                <button
                  type="button"
                  onClick={policyFormStep === 1 ? handleCancelPolicy : goToPreviousPolicyStep}
                  className={`px-4 py-2 text-sm rounded-lg ${
                    darkMode ? "bg-[hsl(var(--ds-muted))] text-white hover:bg-[hsl(var(--ds-muted))]" : "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                  }`}
                >
                  {policyFormStep === 1 ? "Cancel" : "Previous"}
                </button>
                {policyFormStep < POLICY_FORM_STEPS.length ? (
                  <button
                    type="button"
                    onClick={goToNextPolicyStep}
                    className="px-4 py-2 text-sm rounded-lg inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 hover:opacity-95 shadow-sm"
                  >
                    Next: {POLICY_FORM_STEPS[policyFormStep]?.label}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={requestSavePolicyForm}
                    disabled={savingPolicy}
                    className="px-4 py-2 text-sm rounded-lg inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 hover:opacity-95 shadow-sm disabled:opacity-60"
                  >
                    {savingPolicy ? "Saving..." : "Preview & Save Policy"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {deletePolicyModal && (
        <PolicyDeleteImpactModal
          open
          mode={deletePolicyModal.mode}
          impact={deletePolicyModal.impact}
          policyName={deletePolicyModal.policyName}
          deleting={deletePolicyLoading}
          onClose={() => setDeletePolicyModal(null)}
          onConfirmDelete={handleConfirmDeletePolicy}
        />
      )}
      {saveGateModal?.type === "effective_date" && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10005] p-4">
          <div className="w-[min(100%,480px)] bg-[hsl(var(--ds-surface))] border border-amber-300 shadow-xl rounded-none p-6">
            <h3 className="text-lg font-semibold text-amber-900">Effective date must be updated</h3>
            <p className="text-sm text-[hsl(var(--ds-fg))] mt-3 leading-relaxed">
              You changed policy content (rules, applicability, workflows, etc.) but the <strong>effective date</strong> is
              still the same as the saved version. For audit and employee communication, you must set a new effective
              date—typically a <strong>future</strong> date—whenever the policy wording or configuration changes.
            </p>
            <p className="text-sm text-[hsl(var(--ds-muted-fg))] mt-2">Update the effective date in the form, then save again.</p>
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium rounded-none bg-amber-700 text-white hover:bg-amber-800"
                onClick={() => setSaveGateModal(null)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      {saveGateModal?.type === "overlap" && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10005] p-4">
          <div className="w-[min(100%,560px)] max-h-[85vh] bg-[hsl(var(--ds-surface))] border border-[hsl(var(--ds-border))] shadow-xl rounded-none flex flex-col">
            <div className="px-5 py-4 border-b border-[hsl(var(--ds-border))] bg-amber-50">
              <h3 className="text-base font-semibold text-amber-950">Applicability overlap</h3>
              <p className="text-sm text-[hsl(var(--ds-fg))] mt-2 leading-relaxed">
                Another active policy already covers some of the same employees under this combination of scope (for
                example, &quot;All employees&quot; overlaps a department-only policy, or two policies share the same
                department and employment types). Assigning more than one onboarding policy to the same people can
                cause conflicting rules.
              </p>
              <p className="text-sm font-medium text-[hsl(var(--ds-fg))] mt-2">Saving this policy will be final for this record.</p>
            </div>
            <div className="px-5 py-3 overflow-y-auto flex-1">
              <ul className="space-y-3 text-sm text-[hsl(var(--ds-fg))]">
                {saveGateModal.items.map((item) => (
                  <li key={item.id} className="border border-[hsl(var(--ds-border))] rounded-none p-3 bg-[hsl(var(--ds-muted))]">
                    <p className="font-semibold text-[hsl(var(--ds-fg))]">{item.name}</p>
                    <p className="text-[hsl(var(--ds-muted-fg))] mt-1 text-xs leading-relaxed">{item.detail}</p>
                  </li>
                ))}
              </ul>
            </div>
            <div className="px-5 py-4 border-t border-[hsl(var(--ds-border))] flex flex-wrap gap-2 justify-end bg-[hsl(var(--ds-surface))]">
              <button
                type="button"
                className="px-4 py-2 text-sm rounded-none border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                onClick={() => setSaveGateModal(null)}
              >
                Back to edit
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm rounded-none inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 hover:opacity-95"
                onClick={continueAfterOverlapAck}
              >
                I understand — continue to preview &amp; save
              </button>
            </div>
          </div>
        </div>
      )}
      {showSummaryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10001] p-4">
          <div className="w-[780px] max-w-[95vw] max-h-[85vh] bg-[hsl(var(--ds-surface))] shadow-xl flex flex-col">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h3 className="font-semibold text-[hsl(var(--ds-fg))]">Policy Preview</h3>
              <button onClick={() => setShowSummaryModal(false)} className="text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 overflow-auto">
              <div className="text-sm text-[hsl(var(--ds-fg))] space-y-2">
                {getPolicySummaryRows(newPolicy).map((row, idx) => (
                  <div key={`sum-${idx}`} className="grid grid-cols-3 gap-3 border-b pb-2">
                    <p className="font-medium col-span-1">{row.label}</p>
                    <p className="col-span-2">{String(row.value ?? "—")}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 text-sm text-[hsl(var(--ds-muted-fg))]">
                <strong>Roles that can approve:</strong> {selectedApprovalRoleLabels.join(", ") || "—"}
              </div>
            </div>
            <div className="px-4 py-3 border-t flex justify-end gap-2">
              <button
                onClick={() => setShowSummaryModal(false)}
                className="px-4 py-2 text-sm bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
              >
                Back
              </button>
              <button
                onClick={handleSavePolicy}
                disabled={savingPolicy}
                className="px-4 py-2 text-sm inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 hover:opacity-95 disabled:opacity-60"
              >
                {savingPolicy ? "Saving..." : "Save Policy"}
              </button>
            </div>
          </div>
        </div>
      )}
      {previewPolicy && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10001] p-4">
          <div className="w-[780px] max-w-[95vw] max-h-[85vh] bg-[hsl(var(--ds-surface))] shadow-xl flex flex-col">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h3 className="font-semibold text-[hsl(var(--ds-fg))]">Saved Policy Preview</h3>
              <button onClick={() => setPreviewPolicy(null)} className="text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 overflow-auto text-sm text-[hsl(var(--ds-fg))] space-y-2">
              {getPolicySummaryRows(previewPolicy).map((row, idx) => (
                <div key={`prev-${idx}`} className="grid grid-cols-3 gap-3 border-b pb-2">
                  <p className="font-medium col-span-1">{row.label}</p>
                  <p className="col-span-2">{String(row.value ?? "—")}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {flowchartPolicy && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10001] p-4">
          <div className="w-[min(96vw,1120px)] max-h-[90vh] bg-[hsl(var(--ds-surface))] rounded-xl shadow-2xl border border-[hsl(var(--ds-border))] flex flex-col overflow-hidden">
            <div className="px-4 py-3 border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] flex items-center justify-between gap-3">
              <div>
                <h3 className="font-semibold text-[hsl(var(--ds-fg))] text-sm">
                  Preview flowchart — {flowchartPolicy.policyName || "Policy"}
                </h3>
                <p className="text-[11px] text-[hsl(var(--ds-muted-fg))] mt-0.5">
                  Onboarding request, reviews, decision, and outcomes. Use Print to save as PDF.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => printCombinedFlowchartPdf(flowchartPolicy)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-[hsl(var(--ds-surface))] text-white hover:bg-[hsl(var(--ds-surface))]"
                >
                  <Printer size={14} />
                  Print / Save as PDF
                </button>
                <button
                  type="button"
                  onClick={() => setFlowchartPolicy(null)}
                  className="text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))] p-1"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
            <div className="p-4 overflow-auto bg-[hsl(var(--ds-surface))]">
              <div
                className="w-full overflow-x-auto rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/50 p-3"
                dangerouslySetInnerHTML={{ __html: buildOnboardingProcessSvg(flowchartPolicy) }}
              />
              <p className="text-[11px] text-[hsl(var(--ds-muted-fg))] mt-3">
                Colored connectors match each outcome column (complete, reject, cancel).
              </p>
            </div>
          </div>
        </div>
      )}
      {actionMenu &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[10018]" aria-hidden onClick={() => setActionMenu(null)} />
            <div
              className="fixed z-[10019] w-56 rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] py-1 text-left text-sm shadow-lg dark:border-[hsl(var(--ds-border))] dark:bg-[#1D1D1D]"
              style={{ top: actionMenu.top, left: actionMenu.left }}
              role="menu"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-sm text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))] dark:text-gray-200 dark:hover:bg-[hsl(var(--ds-surface))]"
                onClick={() => {
                  openEditForm(actionMenu.policy);
                  setActionMenu(null);
                }}
              >
                Edit
              </button>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-sm text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))] dark:text-gray-200 dark:hover:bg-[hsl(var(--ds-surface))]"
                onClick={() => {
                  setPreviewPolicy(actionMenu.policy);
                  setActionMenu(null);
                }}
              >
                Preview (configuration)
              </button>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-sm text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))] dark:text-gray-200 dark:hover:bg-[hsl(var(--ds-surface))]"
                onClick={() => openPolicyDocumentPreview(actionMenu.policy)}
              >
                Policy document (HR)
              </button>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-sm text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))] dark:text-gray-200 dark:hover:bg-[hsl(var(--ds-surface))]"
                onClick={() => {
                  setFlowchartPolicy(actionMenu.policy);
                  setActionMenu(null);
                }}
              >
                Flowchart
              </button>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-sm text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))] dark:text-gray-200 dark:hover:bg-[hsl(var(--ds-surface))]"
                onClick={() => {
                  printPolicyDocument(actionMenu.policy);
                  setActionMenu(null);
                }}
              >
                Print (configuration summary)
              </button>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-sm text-[hsl(var(--ds-secondary))] hover:bg-[hsl(var(--ds-muted))] dark:text-[hsl(var(--ds-accent-fg))] dark:hover:bg-[hsl(var(--ds-surface))]"
                onClick={() => {
                  if (
                    window.confirm(
                      "Publish this policy document to employees covered by this policy (assigned scope)?"
                    )
                  ) {
                    handlePushPolicyDocument(actionMenu.policy);
                  }
                }}
              >
                Push policy document
              </button>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-sm text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))] dark:text-gray-200 dark:hover:bg-[hsl(var(--ds-surface))]"
                onClick={() => {
                  handleCopyPolicy(actionMenu.policy);
                  setActionMenu(null);
                }}
              >
                Copy
              </button>
              {actionMenu.policy.status === "active" ? (
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left text-sm text-amber-800 hover:bg-amber-50 dark:text-amber-300 dark:hover:bg-[hsl(var(--ds-surface))]"
                  onClick={() => {
                    handleDeactivatePolicy(actionMenu.policy.id);
                    setActionMenu(null);
                  }}
                >
                  Deactivate
                </button>
              ) : (
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left text-sm text-emerald-800 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-[hsl(var(--ds-surface))]"
                  onClick={() => {
                    handleReactivatePolicy(actionMenu.policy.id);
                    setActionMenu(null);
                  }}
                >
                  Reactivate
                </button>
              )}
              <button
                type="button"
                className="w-full border-t border-[hsl(var(--ds-border))] px-3 py-2 text-left text-sm text-[hsl(var(--ds-primary))] hover:bg-rose-50 dark:border-[hsl(var(--ds-border))] dark:text-rose-400 dark:hover:bg-[hsl(var(--ds-surface))]"
                onClick={() => {
                  handleDeletePolicy(actionMenu.policy.id);
                  setActionMenu(null);
                }}
              >
                Delete
              </button>
            </div>
          </>,
          document.body
        )}
      {policyDocModal &&
        (() => {
          const docPolicy = policies.find((p) => p.id === policyDocModal.id) || policyDocModal;
          const docHtml = buildOnboardingPolicyDocumentHtml(docPolicy, policyDocumentOptions);
          return (
            <div className="fixed inset-0 z-[10002] flex items-center justify-center p-4 bg-black/50">
              <div className="w-[min(96vw,880px)] max-h-[92vh] bg-[hsl(var(--ds-surface))] flex flex-col border border-[hsl(var(--ds-border))] rounded-none shadow-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-[hsl(var(--ds-border))] flex items-center justify-between gap-3 bg-[hsl(var(--ds-muted))]">
                  <div>
                    <h3 className="font-semibold text-[hsl(var(--ds-fg))] text-sm">Policy document</h3>
                    <p className="text-[11px] text-[hsl(var(--ds-muted-fg))] mt-0.5">
                      {docCompanyName}
                      {docCompanyAddress ? ` · ${docCompanyAddress.split("\n")[0]}` : ""}
                    </p>
                    <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-1 font-medium">{docPolicy.policyName || "Policy"}</p>
                    <p className="text-[11px] text-[hsl(var(--ds-muted-fg))] mt-0.5">
                      Document v{docPolicy.policyVersion ?? 1} · Effective {docPolicy.effectiveDate || "—"}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setPolicyDocModal(null);
                      setPolicyHistoryModalId(null);
                      setHistoryPreviewEntry(null);
                    }}
                    className="text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))] p-1"
                    aria-label="Close"
                  >
                    <X size={18} />
                  </button>
                </div>
                <div className="flex-1 min-h-0 overflow-hidden bg-[hsl(var(--ds-muted))] p-2">
                  <iframe title="Policy document" className="w-full h-[min(70vh,640px)] border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]" srcDoc={docHtml} />
                </div>
                <div className="px-4 py-3 border-t border-[hsl(var(--ds-border))] flex flex-wrap gap-2 justify-between items-center bg-[hsl(var(--ds-surface))]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => {
                        setPolicyHistoryModalId(docPolicy.id);
                        setHistoryPreviewEntry(null);
                      }}
                      className="px-3 py-1.5 text-xs font-medium rounded-none border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                    >
                      View history
                    </button>
                    {docPolicy.policyDocumentPushed && docPolicy.policyPushedAt && (
                      <span className="text-xs text-emerald-800">
                        Published: {new Date(docPolicy.policyPushedAt).toLocaleString()}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 items-center justify-end">
                    <button
                      type="button"
                      onClick={() => printPolicyFullDocument(docPolicy)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-none border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                    >
                      <Printer size={14} />
                      Print document
                    </button>
                    {!docPolicy.policyDocumentPushed ? (
                      <button
                        type="button"
                        onClick={() => {
                          if (
                            window.confirm(
                              "Publish this policy document to employees covered by this policy (assigned scope)?"
                            )
                          ) {
                            handlePushPolicyDocument(docPolicy);
                          }
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-none inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 hover:opacity-95"
                      >
                        Push to assigned employees
                      </button>
                    ) : (
                      <span className="text-xs text-[hsl(var(--ds-muted-fg))]">Already pushed — use Print to share a copy.</span>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setPolicyDocModal(null);
                        setPolicyHistoryModalId(null);
                        setHistoryPreviewEntry(null);
                      }}
                      className="px-3 py-1.5 text-xs font-medium rounded-none border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] hover:bg-[hsl(var(--ds-muted))]"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      {policyHistoryModalId &&
        (() => {
          const histPolicy = policies.find((p) => p.id === policyHistoryModalId);
          if (!histPolicy) return null;
          const rows = getPolicySnapshotsForDisplay(histPolicy);
          const previewHtml = historyPreviewEntry
            ? buildOnboardingPolicyDocumentHtml(historyPreviewEntry.policy, policyDocumentOptions)
            : "";
          return (
            <div className="fixed inset-0 z-[10003] flex items-center justify-center p-4 bg-black/55">
              <div className="w-[min(96vw,900px)] max-h-[92vh] bg-[hsl(var(--ds-surface))] border border-[hsl(var(--ds-border))] rounded-none shadow-xl flex flex-col overflow-hidden">
                <div className="px-4 py-3 border-b border-[hsl(var(--ds-border))] flex justify-between items-start gap-3 bg-[hsl(var(--ds-muted))]">
                  <div>
                    <h3 className="font-semibold text-[hsl(var(--ds-fg))] text-sm">Policy version history</h3>
                    <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-0.5">{histPolicy.policyName || "Policy"}</p>
                    <p className="text-[11px] text-[hsl(var(--ds-muted-fg))] mt-2 max-w-[52rem] leading-relaxed">
                      The document version number increases when you save with a new <strong>effective date</strong> that is
                      later than the previous effective date and is strictly <strong>after today’s date</strong> (future
                      effective date). Ordinary edits without that change keep the same version. Each row below is a stored
                      snapshot of the policy wording for that version — preview or print any version for records or employee
                      communication.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setPolicyHistoryModalId(null);
                      setHistoryPreviewEntry(null);
                    }}
                    className="text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))] p-1 flex-shrink-0"
                    aria-label="Close history"
                  >
                    <X size={18} />
                  </button>
                </div>
                <div className="flex-1 overflow-auto p-4">
                  <table className="w-full text-left text-xs border border-[hsl(var(--ds-border))] border-collapse">
                    <thead>
                      <tr className="bg-[hsl(var(--ds-muted))] border-b border-[hsl(var(--ds-border))]">
                        <th className="px-3 py-2 font-semibold text-[hsl(var(--ds-fg))]">Version</th>
                        <th className="px-3 py-2 font-semibold text-[hsl(var(--ds-fg))]">Effective date</th>
                        <th className="px-3 py-2 font-semibold text-[hsl(var(--ds-fg))]">Recorded at</th>
                        <th className="px-3 py-2 font-semibold text-[hsl(var(--ds-fg))] text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr key={`${row.version}-${row.savedAt}`} className="border-b border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted)/0.35)]">
                          <td className="px-3 py-2 font-medium text-[hsl(var(--ds-fg))]">{row.version}</td>
                          <td className="px-3 py-2 text-[hsl(var(--ds-fg))]">{row.effectiveDate || "—"}</td>
                          <td className="px-3 py-2 text-[hsl(var(--ds-muted-fg))]">
                            {row.savedAt ? new Date(row.savedAt).toLocaleString() : "—"}
                          </td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            <button
                              type="button"
                              className="text-[hsl(var(--ds-secondary))] hover:underline mr-3 font-medium"
                              onClick={() => setHistoryPreviewEntry(row)}
                            >
                              View
                            </button>
                            <button
                              type="button"
                              className="text-[hsl(var(--ds-fg))] hover:underline font-medium"
                              onClick={() => printPolicySnapshotDocument(row.policy)}
                            >
                              Print
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {historyPreviewEntry && (
                    <div className="mt-4 border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] p-2">
                      <p className="text-xs font-semibold text-[hsl(var(--ds-fg))] mb-2">
                        Preview — version {historyPreviewEntry.version} (effective {historyPreviewEntry.effectiveDate || "—"})
                      </p>
                      <iframe
                        title="Policy version preview"
                        className="w-full h-[min(50vh,480px)] border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
                        srcDoc={previewHtml}
                      />
                    </div>
                  )}
                </div>
                <div className="px-4 py-3 border-t border-[hsl(var(--ds-border))] flex justify-end bg-[hsl(var(--ds-surface))]">
                  <button
                    type="button"
                    className="px-3 py-1.5 text-xs font-medium rounded-none border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] hover:bg-[hsl(var(--ds-muted))]"
                    onClick={() => {
                      setPolicyHistoryModalId(null);
                      setHistoryPreviewEntry(null);
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
};

export default OnboardingPolicyComponent;
